import { StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AttributionWatermark } from '../components/AttributionWatermark'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

// Measured off Figma 2812:2233, from the drawer's own top edge.
const FEE_DETAILS_METRICS = {
  // A point under the `body` token, matching the fee line that opens the drawer
  // and the balance beside it — the drawer sat a size larger than both.
  rowSize: 15,
  rowLeading: 22,
  /** Drawer top to the title. */
  titleTop: 68,
  /** Title box to the summary (105 - 99). */
  summaryGap: 6,
  /** Summary box to the first row (183 - 145). */
  rowsGap: 38,
  /** The paragraph takes a shorter measure than the rules: 298 of the 313. */
  summaryInset: 15,
  /** -0.01em on the summary paragraph. */
  summaryTracking: -0.14,
  /** The design's clearance either side of a breakdown rule. */
  dividerGap: 9,
} as const

// The design's tail below the last row (369 - 285) less the home indicator it
// was drawn over (34) — the device's real inset is added back at render.
const BODY_PADDING_BOTTOM = 50

/** One `label · value` line of the breakdown. */
export interface FeeDetailsRow {
  label: string
  /**
   * The run inside `label` the design dims — the fee's rate. Passed as the
   * substring rather than a second string, since locales seat the rate
   * mid-sentence ("Comisión del 0.50%") or lead with it ("0.50% ücret").
   */
  rate?: string
  value: string
  /**
   * The summed line the rest add up to — set in semibold, and the only row the
   * design emphasises.
   */
  isTotal?: boolean
}

export interface FeeDetailsScreenProps {
  /**
   * What the fees are and where they land, in prose. Omitted when the host
   * hasn't named a payment method.
   */
  summary?: string
  rows: FeeDetailsRow[]
  /** False when the host has bought the mark off. */
  showAttribution?: boolean
}

/**
 * The fee disclosure drawer. Opened from the fee line on the amount screen, it
 * presents the transaction's top-level cost: a written summary of the fee, then
 * the amount, the fee and what the two come to.
 *
 * Presented through `Drawer`, which draws the grabber and owns the gesture,
 * the backdrop and accessibility escape — the drawer's only ways out.
 */
export function FeeDetailsScreen({
  summary,
  rows,
  showAttribution = false,
}: FeeDetailsScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const insets = useSafeAreaInsets()

  return (
    <Box>
      <Box
        style={[
          styles.body,
          { paddingBottom: BODY_PADDING_BOTTOM + insets.bottom },
        ]}
      >
        <Text variant="drawerTitle">{t('feeDetails.title')}</Text>
        {summary ? (
          <Text
            variant="detail"
            style={[
              styles.summary,
              { color: theme.content.elements.secondary },
            ]}
          >
            {summary}
          </Text>
        ) : null}

        <Box style={styles.rows}>
          {rows.map((row, index) => (
            // Index-keyed: two rows can legitimately share a label (a repeated
            // network fee), so the label is not a key.
            <Box key={index}>
              {index > 0 ? <RowDivider /> : null}
              <Row row={row} />
            </Box>
          ))}
        </Box>
      </Box>
      {showAttribution ? <AttributionWatermark /> : null}
    </Box>
  )
}

function Row({ row }: { row: FeeDetailsRow }) {
  const weight = row.isTotal ? 'semibold' : 'base'
  return (
    <Box flexDirection="row" alignItems="center" justifyContent="space-between">
      <RowLabel row={row} />
      <Text
        variant="body"
        weight={weight}
        numberOfLines={1}
        style={[styles.rowText, styles.rowValue]}
      >
        {row.value}
      </Text>
    </Box>
  )
}

/** The label, with the fee's rate dimmed where the row carries one. */
function RowLabel({ row }: { row: FeeDetailsRow }) {
  const theme = useTheme()
  const weight = row.isTotal ? 'semibold' : 'base'
  const at = row.rate === undefined ? -1 : row.label.indexOf(row.rate)

  if (row.rate === undefined || at < 0) {
    return (
      <Text
        variant="body"
        weight={weight}
        numberOfLines={1}
        style={styles.rowText}
      >
        {row.label}
      </Text>
    )
  }

  return (
    <Text
      variant="body"
      weight={weight}
      numberOfLines={1}
      style={styles.rowText}
    >
      {row.label.slice(0, at)}
      {/* Restyle falls back to the `defaults` variant on a nested node, so the
          rate has to be re-sized or it renders a point larger than its row. */}
      <Text
        style={[styles.rowText, { color: theme.content.elements.secondary }]}
      >
        {row.rate}
      </Text>
      {row.label.slice(at + row.rate.length)}
    </Text>
  )
}

/** Hairline between breakdown rows (Figma `Line 1`/`Line 2`, 313pt wide). */
function RowDivider() {
  const theme = useTheme()
  return (
    <Box
      style={[
        styles.divider,
        { backgroundColor: theme.border.elements.default.color },
      ]}
    />
  )
}

const styles = StyleSheet.create({
  // The drawer's own 40pt gutter — wider than a flow screen's, and the width
  // the dividers span (393 − 40 − 40 = 313 in the design).
  body: {
    paddingHorizontal: 40,
    paddingTop: FEE_DETAILS_METRICS.titleTop,
  },
  summary: {
    marginTop: FEE_DETAILS_METRICS.summaryGap,
    marginRight: FEE_DETAILS_METRICS.summaryInset,
    letterSpacing: FEE_DETAILS_METRICS.summaryTracking,
  },
  rows: { marginTop: FEE_DETAILS_METRICS.rowsGap },
  rowText: {
    fontSize: FEE_DETAILS_METRICS.rowSize,
    lineHeight: FEE_DETAILS_METRICS.rowLeading,
  },
  // Equal-advance digits, so the figures line up down the value column.
  rowValue: { fontVariant: ['tabular-nums'] },
  // A zero-height line in the design; a full point would print three device
  // pixels and push the rows off their 40pt pitch.
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: FEE_DETAILS_METRICS.dividerGap,
  },
})
